import { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Checkbox, Form } from "antd";
import { FormContainer } from "../InmateDetails/styles";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import {
  formatDropDownOptions,
  formatDropDownOptionsWithRefListId,
  getOptionsFromRefList,
  getRefListItemGlobalIdByDefaultString,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import dayjs from "dayjs";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { labels } from "@/util/label";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import Select from "@/common/elements/InputElements/Select";
import CommonDatePicker from "@/common/elements/DatePicker";
import BusinessInformation from "../AddInmateMovement/BusinessInformation";
import { INMATE_DETAILS_MODE } from "../InmateDetails";
import TextField from "@/common/elements/InputElements/TextField";
import Button from "@/common/elements/Button";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import TransportDocumentModal from "../InmateDetails/TransportDocumentModal";
import BlockSuggestionPopup from "./BlockSuggestion";
import CellSuggestionPopup from "./CellSuggestion";
import { SCHEMA_NAME } from "@/util/common_variable";
import DynamicForm from "@/common/DynamicForm";
import { BookingsSectionTitle } from "../BookingsDetails/styles";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

const InmateMovementDetails = forwardRef(
  (
    {
      data,
      bookingsFieldsEnabled,
      mode,
      isScheduled = false,
      isReturn = false,
      newFormDisable,
      isInLoan = false,
      startingPoint = "",
      isCancelTransport = false,
    },
    ref
  ) => {
    const [form] = Form.useForm();
    const fullSearch = useSelector((state) => state.fullSearch);
    const [isCancelTransportMode, setIsCancelTransportMode] = useState(isCancelTransport);

    const [inmateTrack, setInmateTrack]               = useState();
    const [isTransportModal, setIsTransportModal]     = useState(false);
    const [suggestionData, setSuggestionData]         = useState([]);
    const [showBlockSuggestion, setShowBlockSuggestion] = useState(false);
    const [showCellSuggestion, setShowCellSuggestion] = useState(false);
    const [blockOptions, setBlockOptions]             = useState();
    const [cellOptions, setCellOptions]               = useState();
    const [bedOptions, setBedOptions]                 = useState();
    const [destinationOptions, setDestinationOptions] = useState();
    const [movementstatus, setMovementStatus]         = useState([]);

    // -----------------------------------------------------------------------
    // InmateTrackingExtra — PCI's per-client dynamic "_U" columns for this
    // movement (InmateTrackingInterface.asp's "Agency-Specific Movement
    // Information" section, shown right after Business Information).
    // -----------------------------------------------------------------------
    const [inmateTrackingExtraFields, setInmateTrackingExtraFields] = useState([]);
    const [inmateTrackingExtraFieldsReady, setInmateTrackingExtraFieldsReady] =
      useState(false);
    const inmateTrackingExtraTypeMapRef = useRef({});
    const inmateTrackingExtraIdRef = useRef(0);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/InmateTrackingExtra`)
        .then((response) => {
          const tableData = (response?.data || []).filter((d) =>
            d?.columnName?.endsWith("_U")
          );
          const converted = convertApiFields(tableData, "InmateTrackingExtra");
          const typeMap = {};
          converted.forEach((c) => {
            typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
          });
          inmateTrackingExtraTypeMapRef.current = typeMap;
          setInmateTrackingExtraFields(converted);
          setInmateTrackingExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error(
            "[InmateMovementDetails] InmateTrackingExtra field defs fetch failed:",
            error
          );
        });
    }, []);

    // Fetches the saved InmateTrackingExtra row when editing an existing
    // movement — a brand new movement has no inmateTrackingID yet.
    useEffect(() => {
      if (!inmateTrackingExtraFieldsReady) return;
      const id = data?.inmateTrackingID;
      if (!isNonEmptyVariable(id)) {
        inmateTrackingExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord("InmateTrackingExtra", "inmateTrackingID", id)
        .then((result) => {
          if (!result) return;
          inmateTrackingExtraIdRef.current =
            result.raw?.InmateTrackingExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = inmateTrackingExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          form.setFieldsValue(withDates);
        })
        .catch((error) => {
          console.error(
            "[InmateMovementDetails] InmateTrackingExtra fetch failed:",
            error
          );
        });
    }, [data?.inmateTrackingID, inmateTrackingExtraFieldsReady]);

    // Watched form fields for conditional rendering
    const movementStatus = Form.useWatch("movementStatus", form);
    const scheduleDateTime = Form.useWatch("scheduleDateTime", form);
    const inmateLocation = Form.useWatch("inmateLocation", form);
    const inJailType     = Form.useWatch("inJailType",     form);
    const loanType       = Form.useWatch("loanType",       form);
    const enterBusiness  = Form.useWatch("enterBusiness",  form) || false;

    // -----------------------------------------------------------------------
    // isEnabled — master enable flag (complex business rule, not just security)
    // disableFields — secondary disable for fields editable only in Pending state
    // Both fed into FieldSecurityProvider so SecureFormItem respects them.
    // -----------------------------------------------------------------------
    const isEnabled = useMemo(() => {
      if (isCancelTransportMode) return true;  // ← cancel transport always enabled, period
      return (
        (bookingsFieldsEnabled &&
          !newFormDisable &&
          data?.inmateLocation !== "CLEARED" &&
          (data?.movementStatus !== "Complete" ||
            data?.comments === "RETURNING INMATE FROM LOAN TO CELL.") &&
          data?.movementStatus !== "Cancelled") ||
        fullSearch
      );
    }, [isCancelTransportMode, bookingsFieldsEnabled, newFormDisable, data, fullSearch]);

    const disableFields = fullSearch ? false : data?.movementStatus === "Pending";

    // -----------------------------------------------------------------------
    // Sync data → local state with dayjs conversions
    // -----------------------------------------------------------------------
    useEffect(() => {
      // A brand-new movement (Schedule Move) defaults to Status = Complete
      // with Actual Date/Time = now, same as picking Complete in the Status
      // dropdown. Skipped while In Loan (Complete isn't offered there) and
      // in Full Search (fields are search criteria, not defaults).
      const defaultComplete =
        !data?.inmateTrackingID &&
        !isNonEmptyVariable(data?.movementStatus) &&
        !isInLoan &&
        !fullSearch;
      setInmateTrack({
        ...data,
        ...(defaultComplete && { movementStatus: "Complete" }),
        expectedBackDateTime: isNonEmptyVariable(data?.expectedBackDate)
          ? dayjs(`${data.expectedBackDate || ""} ${data.expectedBackTime || ""}`)
          : null,
        scheduleDateTime: isNonEmptyVariable(data?.scheduledDate)
          ? dayjs(`${data.scheduledDate || ""} ${data.scheduledTime || ""}`)
          : null,
        // Mirrors ASP's InmateTrackingInterface.asp: Actual Date/Time starts
        // blank on a brand-new movement and is only populated once the user
        // picks Status = Complete/In Transit (see the onSelect handler on
        // movementStatus below) - it's not a static "now" default. Eagerly
        // defaulting to dayjs() here pre-filled the field even while Move To
        // and Status were both still unselected (confirmed via screenshot
        // 2026-10-01). With the Complete default above, a new movement now
        // gets "now" here because Status starts as Complete.
        actualDateTime: isNonEmptyVariable(data?.actualDate)
          ? dayjs(`${data.actualDate || ""} ${data.actualTime || ""}`)
          : defaultComplete
          ? dayjs()
          : null,
        businesses: data?.enterBusiness ? data?.businesses : null,
        returnBackDateTime:
          data?.returnDate && data?.returnTime
            ? dayjs(`${data.returnDate} ${data.returnTime}`)
            : isReturn
            ? dayjs()
            : null,
      });
    }, [data]);

    useEffect(() => {
      isNonEmptyVariable(inmateTrack)
        ? form.setFieldsValue(inmateTrack)
        : form.resetFields();
    }, [inmateTrack]);

    useEffect(() => {
      const mvnt = getOptionsFromRefList("Movement_Status");
      // Filter out "Complete" when in loan
      setMovementStatus(isInLoan ? mvnt.filter((e) => e?.value !== "Complete") : mvnt);
    }, [isInLoan, isScheduled]);

    useEffect(() => {
      setIsCancelTransportMode(isCancelTransport);
    }, [isCancelTransport]);

    const handleTransportDocument = () => {
      setIsTransportModal(!isTransportModal);
    };

    // -----------------------------------------------------------------------
    // Expose form API to parent via ref
    // -----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      submitForm:        () => form.submit(),
      resetForm:         () => form.resetFields(),
      getFormValues:     () => form.getFieldsValue(),
      setFormValue:      () => form.setFieldValue(),
      getFieldsError:    () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields:    () => form.validateFields(),
      inmateTrackingExtraFieldKeys: inmateTrackingExtraFields.map((f) => f.columnName),
      inmateTrackingExtraTypeMap: inmateTrackingExtraTypeMapRef.current,
      inmateTrackingExtraID: inmateTrackingExtraIdRef.current,
    }));

    // -----------------------------------------------------------------------
    // Cell / Bed / Destination handlers
    // -----------------------------------------------------------------------
    const handlecellBedListOnChange = (value, field) => {
      if (!isNonEmptyVariable(value)) return;
      const url =
        field === "blockList"
          ? value === "Holding"
            ? `${API_ENDPOINTS.cellBedList}/holdingCellList`
            : `${API_ENDPOINTS.cellBedList}/blockList`
          : `${API_ENDPOINTS.cellBedList}/${field}/${value}`;

      api.get(url).then(({ data }) => {
        if (field === "blockList")      setBlockOptions(formatOptions(data, field));
        else if (field === "cellList")  setCellOptions(formatDropDownOptions(data));
        else                            setBedOptions(formatDropDownOptions(data));
      });
    };

    const formatOptions = (items = [], field) => {
      const filtered = [
        { refListItemGlobalID: "", defaultString: "", longString: "", enabled: true },
        ...(items.filter((rli) => rli?.enabled) || []),
      ];
      if (field === "blockList") {
        return filtered
          .filter((aa) => aa?.defaultString)
          .map((aa) => ({ value: aa.defaultString, enabled: aa?.enabled, label: aa?.longString || aa.defaultString }))
          .sort((a, b) => a.value.localeCompare(b.value));
      }
      return formatDropDownOptions(filtered);
    };

    const handleDestinationListOnChange = (value, option) => {
      // loanType's form value must stay the defaultString ("Court", "T-1", …) —
      // transportAutoCreate.js and the Court/Program conditionals below compare
      // against those strings. The numeric ID needed for this lookup comes from
      // the selected option instead, not from the field value itself.
      const globalId = option?.refListItemGlobalId;
      if (!isNonEmptyVariable(globalId)) return;
      api.get(`${API_ENDPOINTS.destinationList}/${globalId}`).then(({ data }) => {
        setDestinationOptions(formatDropDownOptionsWithRefListId(data, true));
      });
    };

    const handleAutoFill = () => {
      api.get(API_ENDPOINTS.HousingSuggestion).then(({ data }) => {
        form.setFieldValue("block", data?.block);
        form.setFieldValue("cell",  data?.cell);
        form.setFieldValue("bed",   data?.bed);
      });
    };

    const handleSuggestion = (location) => {
      const block = form.getFieldValue("block");
      if (!block) return;

      const payload = [{ filterKey: "housingAssignmentBlock", value: block }];
      if (location === "cell") {
        const cell = form.getFieldValue("cell");
        if (!cell) return;
        payload.push({ filterKey: "housingAssignmentCell", value: cell });
      }

      location === "cell" ? setShowCellSuggestion(true) : setShowBlockSuggestion(true);

      api.post(API_ENDPOINTS.cellBlockSuggestion, payload)
        .then(({ data }) => { if (data) setSuggestionData(data); })
        .catch(() => {});
    };

    // Near the top of the component, after the useSelector calls
    const userAgency = useMemo(() => {
      const userLocal = JSON.parse(localStorage.getItem("userDetails") || "{}");
      return userLocal?.staff?.organization?.[0]?.agency || null;
    }, []);

    const permHousingDestinationOptions = useMemo(() => {
      return getOptionsFromRefList("HOUSING_AGENCY").filter(
        (opt) => opt?.value !== userAgency
      );
    }, [userAgency]);

    // Transport destination: Jail <-> Workhouse only send to each other;
    // Boarding (or any other/unknown agency) can send to either.
    const transportDestinationOptions = useMemo(() => {
      const allowedValues =
        userAgency === "Jail"
          ? ["Workhouse"]
          : userAgency === "Workhouse"
          ? ["Jail"]
          : ["Jail", "Workhouse"];
      return getOptionsFromRefList("HOUSING_AGENCY").filter((opt) =>
        allowedValues.includes(opt?.value)
      );
    }, [userAgency]);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
      <>
        <FormContainer>
          {/*
            bookingsFieldsEnabled={isEnabled} — passes the full business-rule
            enable flag into the provider so SecureFormItem inherits it.
            Fields that also depend on `disableFields` use overrideDisabled={disableFields}.
          */}
          <FieldSecurityProvider
            entity={SCHEMA_NAME.INMATE_TRACKING}
            startingPoint={startingPoint}
            bookingsFieldsEnabled={isEnabled}
          >
            <Form form={form} initialValues={inmateTrack} disabled={!isEnabled} >
              <StyledGridRow gutter={[16]}>

                {/* Move To */}
                <StyledGridCol span={6}>
                  <SecureFormItem
                    fieldName="inmateLocation"
                    overrideDisabled={isCancelTransport ? true : disableFields}
                    formItemProps={{
                      name: "inmateLocation", label: labels.moveTo,
                      required: !fullSearch,
                      rules: !fullSearch ? [{ required: true, message: "Move To is required" }] : [],
                    }}
                  >
                    <Select showSearch variant="outlined" options={getOptionsFromRefList("Inmate_Location")} />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Status — stays enabled while Pending so it can be moved out of Pending, same as scheduleDateTime */}
                <StyledGridCol span={6}>
                  <SecureFormItem
                    fieldName="movementStatus"
                    overrideDisabled={isCancelTransport}
                    formItemProps={{
                      name: "movementStatus", label: labels.status,
                      required: !fullSearch,
                      rules: !fullSearch ? [{ required: true, message: "Status is required" }] : [],
                    }}
                  >
                    <Select
                      onSelect={(value) => {
                        // Mirrors ASP's MovementStatusChanged exactly: only
                        // clear+hide Scheduled Date/Time on leaving Pending
                        // if it was already incomplete - if both date and
                        // time were filled in, ASP leaves the field visible
                        // and populated (confirmed via screenshot 2026-10-01:
                        // ASP keeps showing "10/1/2026 13:00" after switching
                        // to Complete; Imats was wiping/hiding it instead).
                        const schedule = form.getFieldValue("scheduleDateTime");
                        if (value === "Cancelled" || value === "Pending") {
                          form.setFieldValue("actualDateTime", null);
                          if (!schedule) form.setFieldValue("scheduleDateTime", null);
                        } else if (value === "Complete" || value === "In Transit") {
                          if (!schedule) form.setFieldValue("scheduleDateTime", null);
                          if (!fullSearch) form.setFieldValue("actualDateTime", dayjs());
                        }
                      }}
                      showSearch variant="outlined" options={movementstatus} required
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Schedule Date/Time — shown while Pending, OR once already
                    fully filled in (ASP only hides it on leaving Pending if
                    it was still incomplete - see the onSelect handler above). */}
                <StyledGridCol span={6}>
                  {(movementStatus === "Pending" || isNonEmptyVariable(scheduleDateTime)) && (
                    <SecureFormItem
                      fieldName="scheduleDateTime"
                      formItemProps={{
                        name: "scheduleDateTime", label: labels.schduleDateTime,
                        rules: !fullSearch ? [{ required: true, message: "Schedule date & time is required" }] : [],
                      }}
                    >
                      <CommonDatePicker
                        placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" showTime
                        style={{ border: "2px solid rgba(82, 140, 181, 0.27)" }}
                      />
                    </SecureFormItem>
                  )}
                </StyledGridCol>

                {/* Actual Date/Time */}
                <StyledGridCol span={6}>
                  <SecureFormItem
                    fieldName="actualDateTime"
                    overrideDisabled={isCancelTransport ? true : disableFields}
                    formItemProps={{ name: "actualDateTime", label: labels.actualDateTime }}
                  >
                    <CommonDatePicker
                      placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" showTime
                      disableFutureDates style={{ border: "2px solid rgba(82, 140, 181, 0.27)" }}
                    />
                  </SecureFormItem>
                </StyledGridCol>

              </StyledGridRow>

              {/* ── In Jail ───────────────────────────────────────────── */}
              {inmateLocation === "In Jail" && (
                <StyledGridRow gutter={[16]}>
                  <StyledGridCol span={6}>
                    <SecureFormItem
                      fieldName="inJailType"
                      overrideDisabled={isCancelTransportMode ? false : disableFields}
                      formItemProps={{
                        name: "inJailType",
                        label: labels.jailType,
                        rules: !fullSearch ? [{ required: true, message: "Jail Type is required" }] : [],
                      }}
                    >
                      <Select
                        placeholder="Jail Type"
                        showSearch
                        variant="outlined"
                        options={getOptionsFromRefList("Housing_Type")}
                        onChange={(value) => handlecellBedListOnChange(value, "blockList")}
                      />
                    </SecureFormItem>
                  </StyledGridCol>

                  {inJailType === "Cell" && (
                    <>
                      <StyledGridCol span={4}>
                        <SecureFormItem
                          fieldName="block"
                          formItemProps={{
                            name: "block", label: "block",
                            rules: !fullSearch ? [{ required: true, message: "Block is required" }] : [],
                          }}
                        >
                          <Select
                            showSearch variant="outlined"
                            options={Array.isArray(blockOptions) ? blockOptions : getOptionsFromRefList("Block")}
                            onChange={(value) => handlecellBedListOnChange(value, "cellList")}
                          />
                        </SecureFormItem>
                      </StyledGridCol>

                      {/* Suggestion button — action button, not a secured field */}
                      <StyledGridCol span={1}>
                        <FormItem name="block-suggestion" paddingTop="28px">
                          <Button type="secondary" color="secondary" onClick={() => handleSuggestion("block")}>
                            {"."}
                          </Button>
                        </FormItem>
                      </StyledGridCol>

                      <StyledGridCol span={4}>
                        <SecureFormItem
                          fieldName="cell"
                          formItemProps={{ name: "cell", label: "Cell" }}
                        >
                          <Select
                            showSearch variant="outlined"
                            options={Array.isArray(cellOptions) ? cellOptions : getOptionsFromRefList("Cell")}
                            onChange={(value) => handlecellBedListOnChange(value, "bedList")}
                          />
                        </SecureFormItem>
                      </StyledGridCol>

                      {/* Suggestion button — action button, not a secured field */}
                      <StyledGridCol span={1}>
                        <FormItem name="cell-suggestion" paddingTop="28px">
                          <Button type="secondary" color="secondary" onClick={() => handleSuggestion("cell")}>
                            {"."}
                          </Button>
                        </FormItem>
                      </StyledGridCol>

                      <StyledGridCol span={4}>
                        <SecureFormItem
                          fieldName="bed"
                          formItemProps={{ name: "bed", label: labels.bed }}
                        >
                          <Select
                            showSearch variant="outlined"
                            options={Array.isArray(bedOptions) ? bedOptions : getOptionsFromRefList("Bed")}
                          />
                        </SecureFormItem>
                      </StyledGridCol>

                      {/* Auto-fill button — action button, not a secured field */}
                      <StyledGridCol span={1}>
                        <FormItem name="autofill" paddingTop="28px">
                          <Button type="secondary" color="secondary" onClick={handleAutoFill}>
                            {" ? "}
                          </Button>
                        </FormItem>
                      </StyledGridCol>
                    </>
                  )}

                  {inJailType === "Holding" && (
                    <StyledGridRow gutter={16}>
                      <StyledGridCol span={24}>
                        <SecureFormItem
                          fieldName="holdingCell"
                          formItemProps={{
                            name: "holdingCell", label: labels.holdingIslolationCell,
                            rules: !fullSearch ? [{ required: true, message: "Holding / Isolation Cell is required" }] : [],
                          }}
                        >
                          <Select
                            placeholder="Holding/Isolation Cell" showSearch variant="outlined"
                            options={Array.isArray(blockOptions) ? blockOptions : getOptionsFromRefList("Block")}
                          />
                        </SecureFormItem>
                      </StyledGridCol>
                    </StyledGridRow>
                  )}
                </StyledGridRow>
              )}

              {/* ── Loan ─────────────────────────────────────────────── */}
              {inmateLocation === "Loan" && (
                <StyledGridRow gutter={[16]}>

                  <StyledGridCol span={7}>
                    <SecureFormItem
                      fieldName="loanType"
                      overrideDisabled={disableFields}
                      formItemProps={{
                        name: "loanType", label: labels.loanType,
                        rules: !fullSearch ? [{ required: true, message: "Loan Type is required" }] : [],
                      }}
                    >
                      <Select
                        placeholder="Loan Type" showSearch variant="outlined"
                        options={getOptionsFromRefList("Loan_Type")}
                        onChange={handleDestinationListOnChange}
                      />
                    </SecureFormItem>
                  </StyledGridCol>

                  {loanType !== "Court" && loanType !== "Program" && (
                    <StyledGridCol span={8}>
                      <SecureFormItem
                        fieldName="loanDestination"
                        overrideDisabled={disableFields}
                        formItemProps={{
                          name: "loanDestination", label: labels.destination,
                          rules: !fullSearch ? [{ required: true, message: "Loan destination is required" }] : [],
                        }}
                      >
                        <Select
                          showSearch placeholder="Destination" variant="outlined"
                          options={Array.isArray(destinationOptions) ? destinationOptions : getOptionsFromRefList("Loan_Destination")}
                        />
                      </SecureFormItem>
                    </StyledGridCol>
                  )}

                  {loanType === "Court" && (
                    <StyledGridCol span={8}>
                      <SecureFormItem
                        fieldName="court"
                        formItemProps={{
                          name: "court", label: "Court",
                          rules: !fullSearch ? [{ required: true, message: "Court is required" }] : [],
                        }}
                      >
                        <Select showSearch placeholder="Court" variant="outlined" options={getOptionsFromRefList("Court")} />
                      </SecureFormItem>
                    </StyledGridCol>
                  )}

                  {loanType === "Program" && (
                    <StyledGridCol span={8}>
                      <SecureFormItem
                        fieldName="programName"
                        formItemProps={{
                          name: "programName", label: "Program",
                          rules: !fullSearch ? [{ required: true, message: "Program is required" }] : [],
                        }}
                      >
                        <Select showSearch placeholder="Program" variant="outlined" options={getOptionsFromRefList("Program_Name")} />
                      </SecureFormItem>
                    </StyledGridCol>
                  )}

                  <StyledGridCol span={9}>
                    {/*
                      expectedBackDateTime — special case: disabled={!isEnabled && !isReturn}
                      meaning it's enabled when isReturn=true even if !isEnabled.
                      SecureFormItem can't express "un-disable from child", so we keep
                      plain FormItem here and pass disabled directly.
                    */}
                    <FormItem
                      name="expectedBackDateTime"
                      label={labels.expectedDateTime}
                      dependencies={["scheduleDateTime", "actualDateTime"]}
                      rules={!fullSearch ? [
                        { required: true, message: "Expected back date & time is required" },
                        ({ getFieldValue }) => ({
                          // Only checked when a Scheduled Date/Time is present
                          // (i.e. Status = Pending) - mirrors ASP's
                          // ValidateMovementDetailValues.
                          validator(_, value) {
                            const scheduled = getFieldValue("scheduleDateTime");
                            if (!value || !scheduled) return Promise.resolve();
                            if (dayjs(value).isBefore(dayjs(scheduled))) {
                              return Promise.reject(
                                new Error("Expected back date should not be before the scheduled date.")
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                        ({ getFieldValue }) => ({
                          // Mirrors ASP's ValidateInmateMovement.vbs: "The
                          // expected back date & time can NOT be earlier than
                          // the actual date & time" - i.e. Actual cannot be
                          // later than Expected Back. Confirmed via
                          // screenshot 2026-10-01 that Imats wasn't checking
                          // this at all.
                          validator(_, value) {
                            const actual = getFieldValue("actualDateTime");
                            if (!value || !actual) return Promise.resolve();
                            if (dayjs(value).isBefore(dayjs(actual))) {
                              return Promise.reject(
                                new Error(
                                  "The date/time when the inmate is expected back cannot be earlier than the actual date/time when the movement occurred."
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ] : []}
                    >
                      <CommonDatePicker
                        placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" showTime
                        disabled={!isEnabled && !isReturn}
                        style={{ border: "2px solid rgba(82, 140, 181, 0.27)" }}
                      />
                    </FormItem>

                    {/*
                      returnBackDateTime — special case: disabled={(!isEnabled || disableFields) && !isReturn}
                      Same isReturn exception applies — kept as plain FormItem.
                    */}
                    {(isReturn || inmateTrack?.returnBackDateTime) && (
                      <FormItem name="returnBackDateTime" label="Return Date Time">
                        <CommonDatePicker
                          placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" showTime
                          disabled={(!isEnabled || disableFields) && !isReturn}
                          style={{ border: "2px solid rgba(82, 140, 181, 0.27)" }}
                        />
                      </FormItem>
                    )}
                  </StyledGridCol>

                </StyledGridRow>
              )}

              {/* ── Perm Housing Change ───────────────────────────────── */}
              {inmateLocation === "Perm Housing Change" && (
                <StyledGridRow gutter={[16]}>
                  <StyledGridCol span={7}>
                    <SecureFormItem
                      fieldName="transportDestination"
                      overrideDisabled={disableFields}
                      formItemProps={{
                        name: "transportDestination", label: labels.destination,
                        rules: !fullSearch ? [{ required: true, message: "Transport destination is required" }] : [],
                      }}
                    >
                      <Select
                        placeholder="Destination"
                        variant="outlined"
                        showSearch
                        options={permHousingDestinationOptions}
                      />
                    </SecureFormItem>
                  </StyledGridCol>
                  {/* Transport Document — action button, not a secured field */}
                  <StyledGridCol span={5}>
                    <FormItem name="transDoc" label="" paddingTop="30px">
                      <Button type="secondary" color="secondary" onClick={handleTransportDocument}>
                        Transport Document
                      </Button>
                    </FormItem>
                  </StyledGridCol>
                </StyledGridRow>
              )}

              {/* ── Transport ─────────────────────────────────────────── */}
              {inmateLocation === "Transport" && (
                <StyledGridRow gutter={[16]}>
                  <StyledGridCol span={7}>
                    <SecureFormItem
                      fieldName="transportDestination"
                      overrideDisabled={disableFields}
                      formItemProps={{
                        name: "transportDestination", label: labels.destination,
                        rules: !fullSearch ? [{ required: true, message: "Transport destination is required" }] : [],
                      }}
                    >
                      <Select
                        placeholder="Destination" variant="outlined" showSearch
                        options={transportDestinationOptions}
                      />
                    </SecureFormItem>
                  </StyledGridCol>
                  {/* Transport Document — action button, not a secured field */}
                  <StyledGridCol span={5}>
                    <FormItem name="transDoc" label="" paddingTop="30px">
                      <Button type="secondary" color="secondary" onClick={handleTransportDocument}>
                        Transport Document
                      </Button>
                    </FormItem>
                  </StyledGridCol>
                  <StyledGridCol span={8}>
                    <SecureFormItem
                      fieldName="expectedBackDateTime"
                      formItemProps={{ name: "expectedBackDateTime", label: labels.expectedDateTime }}
                    >
                      <CommonDatePicker
                        placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm"
                        style={{ border: "2px solid rgba(82, 140, 181, 0.27)" }}
                      />
                    </SecureFormItem>
                  </StyledGridCol>
                </StyledGridRow>
              )}

              {/* ── Common bottom fields ──────────────────────────────── */}
              <StyledGridRow gutter={[16]}>

                <StyledGridCol span={5}>
                  <SecureFormItem
                    fieldName="moveReason"
                    overrideDisabled={isCancelTransport ? true : disableFields}
                    formItemProps={{ name: "moveReason", label: labels.reasonForMove }}
                  >
                    <Select placeholder="Reason for Move" variant="outlined" showSearch options={getOptionsFromRefList("Move_Reason")} />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol span={6}>
                  <SecureFormItem
                    fieldName="approvingOfficerStaffID"
                    overrideDisabled={isCancelTransport ? true : disableFields}
                    formItemProps={{ name: "approvingOfficerStaffID", label: labels?.approvalOfficer }}
                  >
                    <Select
                      placeholder={labels?.approvalOfficer}
                      readOnly={mode === INMATE_DETAILS_MODE.VIEW}
                      showSearch
                      options={getOptionsFromRefList("", true, true, true)}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol span={6}>
                  <SecureFormItem
                    fieldName="transportingOfficerStaffID"
                    overrideDisabled={isCancelTransport ? true : disableFields}
                    formItemProps={{ name: "transportingOfficerStaffID", label: labels?.transportingOfficer }}
                  >
                    <Select
                      placeholder={labels?.transportingOfficer}
                      readOnly={mode === INMATE_DETAILS_MODE.VIEW}
                      showSearch
                      options={getOptionsFromRefList("", true, true)}
                    />
                  </SecureFormItem>
                </StyledGridCol>

              </StyledGridRow>

              <StyledGridRow>
                <StyledGridCol span={16}>
                  <SecureFormItem
                    fieldName="comments"
                    overrideDisabled={isCancelTransport ? true : disableFields}
                    formItemProps={{ name: "comments", label: labels?.comments }}
                  >
                    <TextField inputType="textarea" placeholder="Comments" variant="outlined" />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>

              <StyledGridRow>
                <StyledGridCol>
                  {/* enterBusiness — plain FormItem since antd Checkbox doesn't accept className safely */}
                  <FormItem
                    name="enterBusiness"
                    valuePropName="checked"
                    label="Business info?"
                    layout="horizontal"
                    className="form-item-checkbox-left"
                    style={{ marginBottom: "0" }}
                  >
                    <Checkbox />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>

              {enterBusiness && <BusinessInformation bookingsFieldsEnabled={isEnabled} />}

              {inmateTrackingExtraFields.length > 0 && (
                <>
                  <BookingsSectionTitle>
                    <span>Agency-Specific Movement Information</span>
                  </BookingsSectionTitle>
                  <StyledGridRow gutter={16}>
                    {inmateTrackingExtraFields.map((sf, index) => (
                      <DynamicForm
                        key={index}
                        formAttributes={sf}
                        form={form}
                        bookingsFieldsEnabled={isEnabled}
                      />
                    ))}
                  </StyledGridRow>
                </>
              )}
            </Form>
          </FieldSecurityProvider>
        </FormContainer>

        {isTransportModal && (
          <TransportDocumentModal show={isTransportModal} onHide={() => setIsTransportModal(false)} />
        )}
        {showBlockSuggestion && (
          <BlockSuggestionPopup
            showBlockSuggestion={showBlockSuggestion}
            setShowBlockSuggestion={setShowBlockSuggestion}
            data={suggestionData} setData={setSuggestionData}
          />
        )}
        {showCellSuggestion && (
          <CellSuggestionPopup
            showCellSuggestion={showCellSuggestion}
            setShowCellSuggestion={setShowCellSuggestion}
            data={suggestionData} setData={setSuggestionData}
          />
        )}
      </>
    );
  }
);

export default InmateMovementDetails;